/**
 * Tiny per-engine identifier quoting helpers used by the data profiling SQL
 * builders. Mirrors the conventions in `src/features/scripting/providers/*`:
 *
 * - MSSQL: `[ident]`
 * - PostgreSQL / Oracle / SQLite: `"ident"` (Oracle/SQLite escape inner `"`)
 * - MySQL: `` `ident` `` (escapes inner ``)
 *
 * Local to data-profile so the script provider classes don't need to be
 * dragged in (they have a much wider surface area).
 */

import type { DatabaseEngineId } from '../../../../core/engines';

export function quoteIdent(engine: DatabaseEngineId, name: string): string {
  switch (engine) {
    case 'mssql':
      return `[${name.replace(/]/g, ']]')}]`;
    case 'mysql':
      return `\`${name.replace(/`/g, '``')}\``;
    case 'pgsql':
    case 'oracle':
    case 'sqlite':
    case 'mock':
    default:
      return `"${name.replace(/"/g, '""')}"`;
  }
}

/**
 * Build a fully qualified table reference. SQLite skips the schema portion
 * (matches existing `sqliteScriptProvider.qualifyTableName`).
 */
export function qualifyTable(
  engine: DatabaseEngineId,
  schema: string,
  table: string,
): string {
  if (engine === 'sqlite' || !schema) return quoteIdent(engine, table);
  return `${quoteIdent(engine, schema)}.${quoteIdent(engine, table)}`;
}

/**
 * Build a per-engine row-limit clause for "first N rows" queries. The result
 * is positioned per the engine's expectation:
 *
 * - Returned `prefix` goes between SELECT and the column list (MSSQL `TOP N`).
 * - Returned `suffix` is appended after ORDER BY (PG/MySQL/SQLite `LIMIT N`,
 *   Oracle `FETCH FIRST N ROWS ONLY`).
 *
 * If you don't need it positioned, prefer `appendRowLimit(sql, ...)`.
 */
export function rowLimitClauses(
  engine: DatabaseEngineId,
  n: number,
): { prefix: string; suffix: string } {
  switch (engine) {
    case 'mssql':
      return { prefix: `TOP ${n} `, suffix: '' };
    case 'oracle':
      return { prefix: '', suffix: ` FETCH FIRST ${n} ROWS ONLY` };
    case 'pgsql':
    case 'mysql':
    case 'sqlite':
    case 'mock':
    default:
      return { prefix: '', suffix: ` LIMIT ${n}` };
  }
}
